import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CreditCard, RefreshCw, PauseCircle, Megaphone, ImageIcon } from "lucide-react";
import { apiClient } from "../../lib/apiClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

function relTime(sec) {
  if (!sec) return "";
  const diff = Math.floor(Date.now() / 1000) - Number(sec);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const KIND_META = {
  payment: { Icon: CreditCard, tint: "text-danger", bg: "bg-danger-bg" },
  renewal: { Icon: RefreshCw, tint: "text-warning", bg: "bg-warning-bg" },
  pause: { Icon: PauseCircle, tint: "text-primary", bg: "bg-primary/10" },
  update: { Icon: Megaphone, tint: "text-primary", bg: "bg-primary/10" },
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ unread: 0, items: [] });
  const [lightbox, setLightbox] = useState(null);
  const panelRef = useRef(null);

  const load = useCallback(() => {
    apiClient.get("/customer/notifications").then(({ data }) => setFeed(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openPanel = () => {
    setOpen(true);
    if (feed.unread > 0) {
      apiClient.post("/customer/notifications/seen").catch(() => {});
      setFeed((f) => ({ ...f, unread: 0 }));
    }
  };

  const handleItem = (it) => {
    if (it.kind === "update" && it.image_url) {
      setLightbox(fullUrl(it.image_url));
      return;
    }
    setOpen(false);
    if (it.link && it.link.startsWith("/customer")) navigate(it.link);
  };

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors active:scale-95 hover:bg-white/10 hover:text-white"
        data-testid="customer-notification-bell"
        aria-label="Notifications"
      >
        <Bell size={17} strokeWidth={1.5} />
        {feed.unread > 0 && (
          <span
            className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white ring-2 ring-primary"
            data-testid="customer-notification-badge"
          >
            {feed.unread > 9 ? "9+" : feed.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60]" data-testid="customer-notification-overlay">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="absolute right-3 top-16 max-h-[70vh] w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
            data-testid="customer-notification-panel"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="font-heading text-sm font-semibold text-text-main">Notifications</p>
              <button type="button" onClick={() => setOpen(false)} data-testid="customer-notification-close" className="text-text-muted hover:text-text-main">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="max-h-[calc(70vh-3rem)] overflow-y-auto">
              {feed.items.length === 0 ? (
                <div className="px-4 py-10 text-center" data-testid="customer-notification-empty">
                  <Bell size={28} strokeWidth={1.25} className="mx-auto text-text-muted/50" />
                  <p className="mt-2 text-sm text-text-muted">You're all caught up.</p>
                </div>
              ) : (
                feed.items.map((it) => {
                  const m = KIND_META[it.kind] || KIND_META.update;
                  const Icon = m.Icon;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => handleItem(it)}
                      className="flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-background active:bg-background"
                      data-testid={`customer-notification-item-${it.id}`}
                    >
                      <span className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${m.bg} ${m.tint}`}>
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold text-text-main">{it.title}</span>
                          {(it.kind === "payment" || it.kind === "renewal") && (
                            <span className="rounded-full bg-danger/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-danger">Priority</span>
                          )}
                        </span>
                        {it.body && <span className="mt-0.5 block text-xs leading-snug text-text-muted">{it.body}</span>}
                        <span className="mt-1 flex items-center gap-2 text-[10px] text-text-muted/70">
                          <span>{relTime(it.created_at)}</span>
                          {it.image_url && <span className="flex items-center gap-0.5 text-primary"><ImageIcon size={10} strokeWidth={2} /> View image</span>}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          data-testid="customer-notification-lightbox"
        >
          <button type="button" className="absolute right-4 top-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={22} strokeWidth={1.5} />
          </button>
          <img src={lightbox} alt="Update" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
        </div>
      )}
    </>
  );
}
